import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n";
import { btn, PageTitle, Spinner } from "../lib/ui";
import { Check, Zap, Clock, Sparkles } from "lucide-react";

const yes = true, no = false;

// Contenido del tarifario único (Start / Pro / Premium) en 5 idiomas.
const CONTENT = {
  es: {
    title: "Planes Lura", intervalMo: "Mensual", intervalYr: "Anual", mo: "/mes", yr: "/año",
    note: "30 días gratis con todo Pro. Pedimos la tarjeta pero no cobramos nada hoy.",
    start: "Empezar 30 días gratis", manage: "Gestionar suscripción",
    soonTitle: "Beta", soonNote: "Beta: acceso completo gratis durante la prueba de 30 días. Las suscripciones empiezan en el lanzamiento y te avisamos antes de cualquier cobro.",
    taglines: { start: "Ordena tu dinero personal", pro: "Controla y factura", premium: "Cuentas en regla con tu gestor" },
    tableTitle: "Qué incluye cada plan", cols: ["Funcionalidad", "Start", "Pro", "Premium"],
    groups: [
      { title: "Base", rows: [
        ["Panel, movimientos y categorías personalizables", yes, yes, yes],
        ["Gastos fijos y variables", yes, yes, yes],
        ["Resúmenes semanal, mensual y anual + gráficos básicos", yes, yes, yes],
        ["País, idioma, moneda e impuesto", yes, yes, yes],
        ["Importación CSV (máx. 100 filas) y exportación CSV", yes, yes, yes],
        ["Espacios", "1 personal", "Personal, Profesional y Visión general", "Multi-espacio"],
        ["Movimientos manuales al mes", "150", "Ilimitados", "Ilimitados"],
      ] },
      { title: "Pro", rows: [
        ["Contratos y facturas", no, yes, yes],
        ["Gráficos filtrables y períodos personalizados", no, yes, yes],
        ["Importación y exportación avanzada (Excel y PDF)", no, yes, yes],
        ["Análisis: gastos hormiga, fugas y previsión", no, yes, yes],
        ["Multimoneda avanzada (conserva la moneda original)", no, yes, yes],
        ["Estimación IVA/TVA y recordatorios", no, yes, yes],
      ] },
      { title: "Premium", rows: [
        ["Acceso para gestor o contable (solo lectura)", no, no, yes],
        ["Balance, P&L y libro registro", no, no, yes],
        ["Colaboradores y auditoría completa", no, no, yes],
        ["Soporte prioritario", no, no, yes],
      ] },
    ],
    soonBoxTitle: "En la hoja de ruta", soonList: ["IVA/TVA para el perfil profesional", "Patrimonio e inversiones (fichas)", "Multiempresa", "Conexión bancaria automática", "Herramientas de gestión de clientes"],
    soonLimit: "",
    affiliate: "El programa de Afiliados es gratuito y está disponible para todos, al margen del plan.",
    usageTitle: "Uso de este mes", movements: "movimientos manuales", unlimited: "Movimientos ilimitados",
    warn80: "Estás cerca del límite mensual de tu plan.", over: "Has alcanzado el límite mensual. Puedes ver y exportar todo; para añadir más, mejora tu plan o espera al mes que viene.",
    trialBadge: "Prueba activa: acceso Pro completo",
  },
  en: {
    title: "Lura plans", intervalMo: "Monthly", intervalYr: "Annual", mo: "/mo", yr: "/yr",
    note: "30 days free with full Pro. We ask for your card but charge nothing today.",
    start: "Start 30 days free", manage: "Manage subscription",
    soonTitle: "Beta", soonNote: "Beta: full access free during the 30-day trial. Subscriptions start at launch and we'll notify you before any charge.",
    taglines: { start: "Organise your personal money", pro: "Control and invoice", premium: "Books in order with your accountant" },
    tableTitle: "What each plan includes", cols: ["Feature", "Start", "Pro", "Premium"],
    groups: [
      { title: "Base", rows: [
        ["Dashboard, movements and custom categories", yes, yes, yes],
        ["Fixed and variable expenses", yes, yes, yes],
        ["Weekly, monthly and yearly summaries + basic charts", yes, yes, yes],
        ["Country, language, currency and tax", yes, yes, yes],
        ["CSV import (max. 100 rows) and CSV export", yes, yes, yes],
        ["Spaces", "1 personal", "Personal, Professional and Overview", "Multi-space"],
        ["Manual movements per month", "150", "Unlimited", "Unlimited"],
      ] },
      { title: "Pro", rows: [
        ["Contracts and invoices", no, yes, yes],
        ["Filterable charts and custom periods", no, yes, yes],
        ["Advanced import and export (Excel and PDF)", no, yes, yes],
        ["Analytics: small leaks, drains and forecast", no, yes, yes],
        ["Advanced multi-currency (keeps original currency)", no, yes, yes],
        ["VAT/TVA estimate and reminders", no, yes, yes],
      ] },
      { title: "Premium", rows: [
        ["Accountant access (read-only)", no, no, yes],
        ["Balance sheet, P&L and ledger", no, no, yes],
        ["Collaborators and full audit", no, no, yes],
        ["Priority support", no, no, yes],
      ] },
    ],
    soonBoxTitle: "On the roadmap", soonList: ["VAT/TVA for the professional profile", "Wealth & investments (records)", "Multi-company", "Automatic bank connection", "Client management tools"],
    soonLimit: "",
    affiliate: "The Affiliate program is free and available to everyone, regardless of plan.",
    usageTitle: "This month's usage", movements: "manual movements", unlimited: "Unlimited movements",
    warn80: "You are close to your plan's monthly limit.", over: "You reached the monthly limit. You can view and export everything; to add more, upgrade or wait until next month.",
    trialBadge: "Trial active: full Pro access",
  },
  fr: {
    title: "Offres Lura", intervalMo: "Mensuel", intervalYr: "Annuel", mo: "/mois", yr: "/an",
    note: "30 jours gratuits avec tout Pro. Nous demandons la carte mais ne débitons rien aujourd'hui.",
    start: "Commencer 30 jours gratuits", manage: "Gérer l'abonnement",
    soonTitle: "Bêta", soonNote: "Bêta : accès complet gratuit pendant l'essai de 30 jours. Les abonnements débutent au lancement et nous vous prévenons avant tout prélèvement.",
    taglines: { start: "Organisez votre argent personnel", pro: "Pilotez et facturez", premium: "Comptes en règle avec votre gestionnaire" },
    tableTitle: "Ce qu'inclut chaque offre", cols: ["Fonctionnalité", "Start", "Pro", "Premium"],
    groups: [
      { title: "Base", rows: [
        ["Tableau de bord, mouvements et catégories personnalisables", yes, yes, yes],
        ["Dépenses fixes et variables", yes, yes, yes],
        ["Résumés hebdo, mensuel et annuel + graphiques de base", yes, yes, yes],
        ["Pays, langue, devise et impôt", yes, yes, yes],
        ["Import CSV (max. 100 lignes) et export CSV", yes, yes, yes],
        ["Espaces", "1 personnel", "Personnel, Professionnel et Vue d'ensemble", "Multi-espace"],
        ["Mouvements manuels par mois", "150", "Illimités", "Illimités"],
      ] },
      { title: "Pro", rows: [
        ["Contrats et factures", no, yes, yes],
        ["Graphiques filtrables et périodes personnalisées", no, yes, yes],
        ["Import et export avancés (Excel et PDF)", no, yes, yes],
        ["Analyses : petites fuites, pertes et prévision", no, yes, yes],
        ["Multidevise avancée (conserve la devise d'origine)", no, yes, yes],
        ["Estimation TVA et rappels", no, yes, yes],
      ] },
      { title: "Premium", rows: [
        ["Accès gestionnaire/comptable (lecture seule)", no, no, yes],
        ["Bilan, compte de résultat et livre de registre", no, no, yes],
        ["Collaborateurs et audit complet", no, no, yes],
        ["Support prioritaire", no, no, yes],
      ] },
    ],
    soonBoxTitle: "Dans la feuille de route", soonList: ["TVA pour le profil professionnel", "Patrimoine et investissements (fiches)", "Multi-entreprise", "Connexion bancaire automatique", "Outils de gestion des clients"],
    soonLimit: "",
    affiliate: "Le programme d'Affiliation est gratuit et disponible pour tous, quel que soit l'offre.",
    usageTitle: "Utilisation ce mois-ci", movements: "mouvements manuels", unlimited: "Mouvements illimités",
    warn80: "Vous approchez de la limite mensuelle de votre offre.", over: "Limite mensuelle atteinte. Vous pouvez tout voir et exporter ; pour en ajouter, passez à une offre supérieure ou attendez le mois prochain.",
    trialBadge: "Essai actif : accès Pro complet",
  },
  pt: {
    title: "Planos Lura", intervalMo: "Mensal", intervalYr: "Anual", mo: "/mês", yr: "/ano",
    note: "30 dias grátis com tudo Pro. Pedimos o cartão mas não cobramos nada hoje.",
    start: "Começar 30 dias grátis", manage: "Gerir subscrição",
    soonTitle: "Beta", soonNote: "Beta: acesso completo grátis durante o período experimental de 30 dias. As subscrições começam no lançamento e avisamos antes de qualquer cobrança.",
    taglines: { start: "Organiza o teu dinheiro pessoal", pro: "Controla e factura", premium: "Contas em ordem com o teu gestor" },
    tableTitle: "O que inclui cada plano", cols: ["Funcionalidade", "Start", "Pro", "Premium"],
    groups: [
      { title: "Base", rows: [
        ["Painel, movimentos e categorias personalizáveis", yes, yes, yes],
        ["Gastos fixos e variáveis", yes, yes, yes],
        ["Resumos semanal, mensal e anual + gráficos básicos", yes, yes, yes],
        ["País, idioma, moeda e imposto", yes, yes, yes],
        ["Importação CSV (máx. 100 linhas) e exportação CSV", yes, yes, yes],
        ["Espaços", "1 pessoal", "Pessoal, Profissional e Visão geral", "Multi-espaço"],
        ["Movimentos manuais por mês", "150", "Ilimitados", "Ilimitados"],
      ] },
      { title: "Pro", rows: [
        ["Contratos e faturas", no, yes, yes],
        ["Gráficos filtráveis e períodos personalizados", no, yes, yes],
        ["Importação e exportação avançada (Excel e PDF)", no, yes, yes],
        ["Análise: gastos formiga, fugas e previsão", no, yes, yes],
        ["Multimoeda avançada (mantém a moeda original)", no, yes, yes],
        ["Estimativa de IVA/TVA e lembretes", no, yes, yes],
      ] },
      { title: "Premium", rows: [
        ["Acesso para gestor ou contabilista (só leitura)", no, no, yes],
        ["Balanço, P&L e livro de registo", no, no, yes],
        ["Colaboradores e auditoria completa", no, no, yes],
        ["Suporte prioritário", no, no, yes],
      ] },
    ],
    soonBoxTitle: "No roteiro", soonList: ["IVA/TVA para o perfil profissional", "Património e investimentos (fichas)", "Multiempresa", "Conexão bancária automática", "Ferramentas de gestão de clientes"],
    soonLimit: "",
    affiliate: "O programa de Afiliados é gratuito e está disponível para todos, independentemente do plano.",
    usageTitle: "Uso deste mês", movements: "movimentos manuais", unlimited: "Movimentos ilimitados",
    warn80: "Estás perto do limite mensal do teu plano.", over: "Atingiste o limite mensal. Podes ver e exportar tudo; para adicionar mais, melhora o plano ou espera pelo próximo mês.",
    trialBadge: "Teste ativo: acesso Pro completo",
  },
  it: {
    title: "Piani Lura", intervalMo: "Mensile", intervalYr: "Annuale", mo: "/mese", yr: "/anno",
    note: "30 giorni gratis con tutto Pro. Chiediamo la carta ma non addebitiamo nulla oggi.",
    start: "Inizia 30 giorni gratis", manage: "Gestisci abbonamento",
    soonTitle: "Beta", soonNote: "Beta: accesso completo gratuito durante la prova di 30 giorni. Gli abbonamenti iniziano al lancio e ti avvisiamo prima di qualsiasi addebito.",
    taglines: { start: "Organizza i tuoi soldi personali", pro: "Controlla e fattura", premium: "Conti in regola con il tuo gestore" },
    tableTitle: "Cosa include ogni piano", cols: ["Funzionalità", "Start", "Pro", "Premium"],
    groups: [
      { title: "Base", rows: [
        ["Pannello, movimenti e categorie personalizzabili", yes, yes, yes],
        ["Spese fisse e variabili", yes, yes, yes],
        ["Riepiloghi settimanale, mensile e annuale + grafici base", yes, yes, yes],
        ["Paese, lingua, valuta e imposta", yes, yes, yes],
        ["Importazione CSV (max 100 righe) ed esportazione CSV", yes, yes, yes],
        ["Spazi", "1 personale", "Personale, Professionale e Panoramica", "Multi-spazio"],
        ["Movimenti manuali al mese", "150", "Illimitati", "Illimitati"],
      ] },
      { title: "Pro", rows: [
        ["Contratti e fatture", no, yes, yes],
        ["Grafici filtrabili e periodi personalizzati", no, yes, yes],
        ["Importazione ed esportazione avanzata (Excel e PDF)", no, yes, yes],
        ["Analisi: piccole spese, perdite e previsione", no, yes, yes],
        ["Multivaluta avanzata (mantiene la valuta originale)", no, yes, yes],
        ["Stima IVA/TVA e promemoria", no, yes, yes],
      ] },
      { title: "Premium", rows: [
        ["Accesso per gestore o commercialista (sola lettura)", no, no, yes],
        ["Bilancio, P&L e libro registro", no, no, yes],
        ["Collaboratori e audit completo", no, no, yes],
        ["Supporto prioritario", no, no, yes],
      ] },
    ],
    soonBoxTitle: "Nella roadmap", soonList: ["IVA per il profilo professionale", "Patrimonio e investimenti (schede)", "Multiazienda", "Connessione bancaria automatica", "Strumenti di gestione clienti"],
    soonLimit: "",
    affiliate: "Il programma Affiliati è gratuito e disponibile per tutti, indipendentemente dal piano.",
    usageTitle: "Utilizzo di questo mese", movements: "movimenti manuali", unlimited: "Movimenti illimitati",
    warn80: "Sei vicino al limite mensile del tuo piano.", over: "Hai raggiunto il limite mensile. Puoi vedere ed esportare tutto; per aggiungerne altri, passa a un piano superiore o aspetta il mese prossimo.",
    trialBadge: "Prova attiva: accesso Pro completo",
  },
};

const PLAN_ORDER = ["start", "pro", "premium"];

const Cell = ({ v }) => {
  if (v === true) return <Check size={16} className="text-[#0299A0] inline" />;
  if (v === false) return <span className="text-[#A8D2C8]">—</span>;
  return <span className="text-[#075E62] text-xs">{v}</span>;
};

export default function Subscription() {
  const { user, refreshMe } = useAuth();
  const { t, locale } = useI18n();
  const readOnly = user?.role === "accountant";
  const C = CONTENT[locale] || CONTENT.es;

  const [sub, setSub] = useState(null);
  const [usage, setUsage] = useState(null);
  const [inTrial, setInTrial] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billingPlans, setBillingPlans] = useState([]);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [billInterval, setBillInterval] = useState("month");

  const startCheckout = async (plan) => {
    try { const { data } = await api.post("/billing/checkout", { plan, interval: billInterval, origin_url: window.location.origin }); window.location.href = data.checkout_url; }
    catch (e) { toast.error(apiError(e)); }
  };
  const openPortal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.portal_url; }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = async () => {
    try {
      const s = await api.get("/subscription");
      setSub(s.data.subscription); setUsage(s.data.usage); setInTrial(!!s.data.in_trial);
      try { const b = await api.get("/billing/plans"); setBillingPlans(b.data.plans || []); setBillingEnabled(!!b.data.enabled); } catch (e) { /* billing opcional */ }
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); refreshMe && refreshMe(); }, []); // eslint-disable-line

  if (loading) return <Spinner />;

  const unlimited = usage?.unlimited;
  const used = usage?.transactions || 0;
  const limit = usage?.limit || C && 150;
  const pct = unlimited ? 0 : Math.min(100, Math.round((used / (limit || 150)) * 100));
  const over = !unlimited && used >= (limit || 150);
  const near = !unlimited && !over && pct >= 80;

  return (
    <div data-testid="subscription-page">
      <PageTitle title={t("p.subscription.t")} subtitle={t("p.subscription.s")} />

      {/* Estado de prueba */}
      {inTrial && (
        <div className="mb-6 rounded-lg border border-[#0299A0]/40 bg-[#0299A0]/10 px-4 py-3 text-sm text-[#075E62] flex items-center gap-2" data-testid="trial-badge">
          <Sparkles size={16} className="text-[#0299A0]" /> {C.trialBadge}
        </div>
      )}

      {/* Bloque Stripe (solo si está configurado) o "Pagos próximamente" */}
      {!billingEnabled && !readOnly && (
        <div className="mb-8 bg-white border border-[#D5E7E5] rounded-lg p-6" data-testid="billing-coming-soon">
          <h3 className="text-base font-bold text-[#075E62] flex items-center gap-2"><Zap size={16} className="text-[#0299A0]" /> {C.soonTitle}</h3>
          <p className="text-sm text-[#5F8588] mt-2">{C.soonNote}</p>
        </div>
      )}

      {billingEnabled && billingPlans.length > 0 && (
        <div className="mb-8 bg-white border border-[#D5E7E5] rounded-lg p-6" data-testid="stripe-billing">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-1">
            <h3 className="text-base font-bold text-[#075E62]">{C.title}</h3>
            <div className="flex gap-1 bg-[#EAF3F1] rounded-full p-1" data-testid="billing-interval-toggle">
              {[["month", C.intervalMo], ["year", C.intervalYr]].map(([v, l]) => (
                <button key={v} data-testid={`billing-interval-${v}`} onClick={() => setBillInterval(v)} className={`px-4 py-1 rounded-full text-sm transition-colors ${billInterval === v ? "bg-[#0299A0] text-white" : "text-[#075E62]"}`}>{l}</button>
              ))}
            </div>
          </div>
          <p className="text-sm text-[#5F8588] mb-4">{C.note}</p>
          <div className="grid md:grid-cols-3 gap-4">
            {billingPlans.map((p) => {
              const price = billInterval === "month" ? p.monthly : p.annual;
              return (
                <div key={p.plan} data-testid={`billing-plan-${p.plan}`} className="border border-[#D5E7E5] rounded-lg p-5 flex flex-col">
                  <h4 className="text-lg font-medium text-[#075E62]">{p.name}</h4>
                  <p className="text-xs text-[#5F8588] mt-0.5 mb-1">{C.taglines[p.plan]}</p>
                  <div className="mt-2 mb-4"><span className="text-3xl font-bold text-[#075E62] tnum">{price.toFixed(2)}</span><span className="text-sm text-[#5F8588]"> {p.currency} {billInterval === "month" ? C.mo : C.yr}</span></div>
                  {!readOnly && <button data-testid={`billing-checkout-${p.plan}`} onClick={() => startCheckout(p.plan)} className={btn.accent + " w-full mt-auto"}>{C.start}</button>}
                </div>
              );
            })}
          </div>
          {sub?.stripe_subscription_id && !readOnly && (
            <button data-testid="billing-portal" onClick={openPortal} className={btn.outline + " mt-4"}>{C.manage}</button>
          )}
        </div>
      )}

      {/* Uso mensual (solo si no es ilimitado/prueba) */}
      {!unlimited && usage && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-5 mb-6" data-testid="usage-box">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-[#075E62]">{C.usageTitle}</span>
            <span className="text-sm text-[#5F8588] tnum">{used} / {limit} {C.movements}</span>
          </div>
          <div className="h-2 rounded-full bg-[#D5E7E5]/50 overflow-hidden">
            <div className={`h-full ${over ? "bg-[#B4533F]" : near ? "bg-[#C98A2B]" : "bg-[#0299A0]"}`} style={{ width: `${pct}%` }} />
          </div>
          {(near || over) && (
            <p className={`text-xs mt-2 ${over ? "text-[#B4533F]" : "text-[#C98A2B]"}`} data-testid="usage-warning">{over ? C.over : C.warn80}</p>
          )}
        </div>
      )}
      {unlimited && (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-4 mb-6 text-sm text-[#075E62] flex items-center gap-2" data-testid="usage-unlimited">
          <Check size={16} className="text-[#0299A0]" /> {C.unlimited}
        </div>
      )}

      {/* Tabla única de funciones Start / Pro / Premium */}
      <div className="mb-8 bg-white border border-[#D5E7E5] rounded-lg p-6" data-testid="plan-features-table">
        <h3 className="text-base font-bold text-[#075E62] mb-1">{C.tableTitle}</h3>
        <div className="grid grid-cols-3 gap-2 mb-4 mt-3">
          {PLAN_ORDER.map((p, i) => (
            <div key={p} className="text-center">
              <div className="text-sm font-bold text-[#075E62]">{C.cols[i + 1]}</div>
              <div className="text-[11px] text-[#5F8588] leading-tight mt-0.5">{C.taglines[p]}</div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[#5F8588] border-b border-[#D5E7E5]">
                <th className="py-2 pr-4 font-medium">{C.cols[0]}</th>
                <th className="py-2 px-3 font-medium text-center w-28">{C.cols[1]}</th>
                <th className="py-2 px-3 font-medium text-center w-28">{C.cols[2]}</th>
                <th className="py-2 px-3 font-medium text-center w-28">{C.cols[3]}</th>
              </tr>
            </thead>
            <tbody>
              {C.groups.map((g) => (
                <React.Fragment key={g.title}>
                  {g.rows.map((r, idx) => (
                    <tr key={g.title + idx} className="border-b border-[#D5E7E5]/60" data-testid="feature-row">
                      <td className="py-2 pr-4 text-[#075E62]">{r[0]}</td>
                      <td className="py-2 px-3 text-center"><Cell v={r[1]} /></td>
                      <td className="py-2 px-3 text-center"><Cell v={r[2]} /></td>
                      <td className="py-2 px-3 text-center"><Cell v={r[3]} /></td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Próximamente */}
      <div className="mb-6 bg-white border border-dashed border-[#A8D2C8] rounded-lg p-6" data-testid="coming-soon-features">
        <h3 className="text-base font-bold text-[#075E62] flex items-center gap-2 mb-3"><Clock size={16} className="text-[#05787E]" /> {C.soonBoxTitle}</h3>
        <ul className="grid sm:grid-cols-2 gap-2 mb-3">
          {C.soonList.map((s) => (
            <li key={s} className="flex items-start gap-2 text-sm text-[#5F8588]"><span className="text-[10px] uppercase tracking-wider text-[#05787E] bg-[#05787E]/10 rounded px-1.5 py-0.5 mt-0.5">roadmap</span> {s}</li>
          ))}
        </ul>
        {C.soonLimit && <p className="text-xs text-[#5F8588]">{C.soonLimit}</p>}
      </div>

      {/* Afiliados */}
      <div className="rounded-lg border border-[#D5E7E5] bg-[#F7FBFA] px-4 py-3 text-sm text-[#5F8588] flex items-center gap-2" data-testid="affiliate-note">
        <Sparkles size={16} className="text-[#0299A0]" /> {C.affiliate}
      </div>
    </div>
  );
}
